import React, { useRef, useState } from 'react';
import type { Language, Product } from '../types';
import type { Dict } from '../shared/i18n';
import { LOCALES } from '../shared/i18n';
import { round2 } from '../shared/domain';
import { canAdd } from '../shared/entitlements';
import type { PlanLimits } from '../shared/config';
import { Card, Empty, LimitNotice } from '../components/shared';

interface ProductsPageProps {
  products: Product[];
  lang: Language;
  t: Dict;
  limits: PlanLimits;
  onUpdate: (products: Product[]) => void;
}

/** Product thumbnails render at ~48px, so a full-resolution photo is pure
 *  waste — and localStorage caps out around 5 MB. Downscale before storing. */
async function downscale(file: File, max = 320): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.7);
}

const blank = { name: '', price: '', category: '', image: undefined as string | undefined };

const ProductsPage: React.FC<ProductsPageProps> = ({ products, lang, t, limits, onUpdate }) => {
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const nf = new Intl.NumberFormat(lang === 'ar' ? 'ar-MA-u-nu-latn' : LOCALES[lang], {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const gate = canAdd(limits, 'products', products.length);

  const handleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const image = await downscale(file);
      setForm((f) => ({ ...f, image }));
    } catch {
      // A HEIC or otherwise undecodable file: keep the product, drop the photo.
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const price = round2(Number(form.price.replace(',', '.')));
    if (!form.name.trim() || !Number.isFinite(price) || price < 0) return;

    const next: Product = {
      id: editing?.id ?? crypto.randomUUID(),
      name: form.name.trim(),
      price,
      category: form.category.trim() || undefined,
      image: form.image,
    };

    onUpdate(editing ? products.map((p) => (p.id === editing.id ? next : p)) : [...products, next]);
    setForm(blank); setOpen(false); setEditing(null);
  };

  const startEdit = (p: Product) => {
    setEditing(p);
    setForm({ name: p.name, price: String(p.price), category: p.category ?? '', image: p.image });
    setOpen(true);
  };

  return (
    <div className="space-y-3 qd-enter">
      {open ? (
        <form onSubmit={submit} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <h2 className="font-extrabold text-slate-900">{editing ? t.saveChanges : t.addProduct}</h2>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-24 h-24 mx-auto block rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 overflow-hidden"
          >
            {form.image
              ? <img src={form.image} alt="" className="w-full h-full object-cover" />
              : <span className="text-xs text-slate-400 font-bold">{t.uploadImage}</span>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />

          <input
            type="text" placeholder={t.productName} value={form.name} required
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
          />
          <input
            type="text" inputMode="decimal" placeholder={t.unitPrice} value={form.price} required
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
          />
          <input
            type="text" placeholder={t.category} value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
          />

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setOpen(false); setEditing(null); setForm(blank); }}
              className="flex-1 bg-slate-100 text-slate-600 py-3 rounded-xl font-extrabold"
            >{t.cancel}</button>
            <button type="submit" className="flex-1 bg-[#006233] text-white py-3 rounded-xl font-extrabold">
              {t.saveProduct}
            </button>
          </div>
        </form>
      ) : gate.allowed ? (
        <button
          onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}
          className="w-full bg-white border-2 border-dashed border-[#C1272D] text-[#C1272D] font-extrabold p-4 rounded-2xl min-h-[56px]"
        >
          ➕ {t.addProduct}
        </button>
      ) : (
        <LimitNotice what="products" t={t} onUpgrade={() => { /* handled by Settings */ }} />
      )}

      {products.length === 0 ? (
        <Empty icon="📦" title={t.noOrders} hint={t.addProduct} />
      ) : (
        products.map((product) => (
          <Card key={product.id} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
                {product.image
                  ? <img src={product.image} alt="" className="w-full h-full object-cover" />
                  : <span className="text-lg" aria-hidden="true">📦</span>}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-slate-900 text-sm truncate">{product.name}</p>
                <p className="text-xs font-extrabold text-[#006233]">
                  {nf.format(product.price)} {t.currency}
                </p>
                {product.category && <p className="text-[10px] text-slate-400 font-bold">{product.category}</p>}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                onClick={() => startEdit(product)}
                aria-label={`${t.edit} ${product.name}`}
                className="w-10 h-10 rounded-xl bg-slate-50"
              >✏️</button>
              <button
                onClick={() => {
                  if (confirm(`${product.name}\n${t.confirmDelete}`)) {
                    onUpdate(products.filter((p) => p.id !== product.id));
                  }
                }}
                aria-label={`${t.delete} ${product.name}`}
                className="w-10 h-10 rounded-xl bg-slate-50"
              >🗑️</button>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

export default ProductsPage;
